// ============================================================
// Presenca.js — o quadradinho de presença, e a legenda dele
//
// Três telas desenhavam a mesma grade, cada uma com a sua cópia das cores
// e do tamanho do quadrado. Os próprios arquivos admitiam isso em
// comentário ("mesmas do GradePresenca"), e era só questão de tempo até
// divergirem — a cópia do preparador já tinha um cinza diferente.
//
// A marca deixou de ser fundo forte com símbolo branco e passou a ser
// fundo claro com LETRA escura. Dois motivos:
//
//   ✓ e ✕ se distinguem pela forma, e numa coluna de 22 pixels a forma é
//   quase a mesma. A cor fazia todo o trabalho, e quem não separa verde de
//   vermelho lia a grade inteira igual. Letra diferente resolve sem
//   depender de cor.
//
//   O fundo forte pintava a tela inteira de verde saturado. Com trinta
//   atletas e vinte dias são seiscentos quadrados, e o que precisa saltar
//   é a falta, não a presença.
//
// Competição e viagem têm letra própria (C e V) porque contam de formas
// diferentes: competição é presença, viagem é abono isento do teto. Antes
// eram 🏆 e ✈️, que mudavam de desenho conforme o aparelho.
// ============================================================

import { RAIO, ESP, CINZA_ESC, BORDA, TEXTO } from "../tema";
import Avatar from "./Avatar";

export const MARCA = {
  presente:    { letra: "P", bg: "#DCFCE7", cor: "#047857", rotulo: "Presente" },
  competicao:  { letra: "C", bg: "#DCFCE7", cor: "#047857", rotulo: "Competição" },
  abonada:     { letra: "J", bg: "#FEF3C7", cor: "#B45309", rotulo: "Abonada" },
  viagem:      { letra: "V", bg: "#FEF3C7", cor: "#B45309", rotulo: "Viagem ou férias" },
  falta:       { letra: "F", bg: "#FEE2E2", cor: "#B91C1C", rotulo: "Falta" },
  semTreino:   { letra: "·", bg: "#F1F5F9", cor: "#94A3B8", rotulo: "Sem treino" },
  // O dia de hoje antes das 18h30. Tracejado, e não cinza cheio: cinza
  // cheio é feriado, e o registro deste dia ainda pode chegar.
  aguardando:  { letra: "", bg: "#FFFFFF", cor: "#94A3B8", rotulo: "Ainda pode registrar", tracejado: true },
  naoMarcado:  { letra: "", bg: "#FFFFFF", cor: "#94A3B8", rotulo: "Não marcado", borda: true },
};

export function Marca({ tipo, titulo, onClick, clicavel = false, estreito = false }) {
  const m = MARCA[tipo] || MARCA.semTreino;
  return (
    <span
      onClick={onClick}
      title={titulo || m.rotulo}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: estreito ? 23 : 26, height: estreito ? 21 : 23,
        borderRadius: 6, boxSizing: "border-box",
        background: m.bg, color: m.cor, fontSize: 11.5, fontWeight: 700,
        border: m.tracejado ? "1.5px dashed #CBD5E1" : m.borda ? `1px solid ${BORDA}` : "1px solid transparent",
        cursor: clicavel ? "pointer" : "default", userSelect: "none",
      }}
    >
      {m.letra}
    </span>
  );
}

// A legenda mostra o mesmo quadrado da grade, com a mesma letra dentro.
// Bolinha colorida ao lado do texto obrigava a traduzir duas vezes: da
// bolinha para a cor, e da cor para o quadrado lá em cima.
export function LegendaDePresenca({ tipos }) {
  return (
    <div style={{
      display: "flex", gap: ESP.lg, flexWrap: "wrap", marginTop: ESP.md,
      fontSize: 12, color: CINZA_ESC, alignItems: "center",
    }}>
      {tipos.map((t) => {
        const m = MARCA[t.tipo] || MARCA.semTreino;
        return (
          <span key={t.tipo} style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
            <Marca tipo={t.tipo} />
            {t.rotulo || m.rotulo}
          </span>
        );
      })}
    </div>
  );
}

// ── A moldura da grade ──────────────────────────────────────
// Rola de lado dentro da própria caixa; a coluna do nome e a do percentual
// ficam presas nas pontas, senão quem rola até o dia 25 perde de vista de
// quem é a linha.
export const GRADE = {
  caixa: { overflowX: "auto", border: `1px solid ${BORDA}`, borderRadius: RAIO.md },
  tabela: { borderCollapse: "collapse", fontSize: 12.5, minWidth: "100%" },
  cabecalho: { background: "#F1F5F9" },
  thNome: {
    textAlign: "left", padding: "11px 14px", color: "#334155", fontWeight: 700,
    fontSize: 12.5, position: "sticky", left: 0, background: "#F1F5F9", zIndex: 2,
    whiteSpace: "nowrap", borderBottom: `1px solid ${BORDA}`,
  },
  thDia: {
    padding: "8px 3px", color: "#64748B", fontWeight: 600, fontSize: 10.5,
    lineHeight: 1.35, minWidth: 34, background: "#F1F5F9",
    borderBottom: `1px solid ${BORDA}`,
  },
  thPct: {
    padding: "11px 14px", color: "#334155", fontWeight: 700, fontSize: 12.5,
    position: "sticky", right: 0, background: "#F1F5F9", zIndex: 2,
    borderBottom: `1px solid ${BORDA}`,
  },
  linha: { borderTop: "1px solid #EFF3F8" },
  tdNome: {
    padding: "9px 14px", position: "sticky", left: 0, background: "#fff",
    whiteSpace: "nowrap", zIndex: 1,
  },
  tdDia: { textAlign: "center", padding: "3px 2px" },
  tdPct: {
    padding: "9px 14px", fontWeight: 800, fontSize: 13, position: "sticky",
    right: 0, background: "#fff", whiteSpace: "nowrap",
  },
};

// Os mesmos estilos, ajustados para tela estreita.
//
// No celular a coluna do nome e a do percentual ficavam presas nas duas
// bordas e sobrava menos de um terço da largura para os dias, que é o que
// a grade existe para mostrar. O nome continua preso, porque sem ele não
// se sabe de quem é a linha ao rolar de lado; o percentual solta e viaja
// com os dias, e ele fica no fim da rolagem, que é onde o olho chega
// depois de percorrer o mês.
export function estilosDaGrade(estreito = false) {
  if (!estreito) return GRADE;
  return {
    ...GRADE,
    thNome: { ...GRADE.thNome, padding: "10px 8px" },
    tdNome: { ...GRADE.tdNome, padding: "8px 8px" },
    thDia: { ...GRADE.thDia, minWidth: 27, padding: "8px 2px" },
    thPct: { ...GRADE.thPct, position: "static", padding: "10px 10px" },
    tdPct: { ...GRADE.tdPct, position: "static", padding: "8px 10px" },
  };
}

// O nome na coluna presa.
//
// Em tela estreita fica só o primeiro nome: o nome inteiro ocupava metade
// da largura do celular, e dentro de uma modalidade o primeiro nome já
// identifica. O nome completo continua no title, e a linha de apoio (a
// modalidade) sai, porque quem está numa grade de modalidade já sabe qual.
export function NomeNaGrade({ nome, apoio, estreito = false }) {
  const partes = String(nome || "").trim().split(/\s+/).filter(Boolean);
  const mostrado = estreito ? (partes[0] || nome) : nome;
  return (
    <span title={nome} style={{ display: "inline-flex", alignItems: "center", gap: estreito ? 7 : 10, minWidth: 0 }}>
      <Avatar nome={nome} tamanho={estreito ? 26 : 32} />
      <span style={{ lineHeight: 1.3, minWidth: 0 }}>
        <span style={{
          display: "block", fontSize: estreito ? 12.5 : 13, fontWeight: 600,
          color: TEXTO, whiteSpace: "nowrap",
        }}>{mostrado}</span>
        {apoio && !estreito && (
          <span style={{ display: "block", fontSize: 11, color: CINZA_ESC }}>{apoio}</span>
        )}
      </span>
    </span>
  );
}

// O rótulo da coluna: o dia em cima, o dia da semana embaixo.
export function CabecalhoDoDia({ num, semana }) {
  return (
    <>
      {num}
      <br />
      <span style={{ color: "#94A3B8", fontWeight: 500 }}>{semana}</span>
    </>
  );
}

// A cor do percentual segue a régua dos relatórios: abaixo de 75 é
// problema, de 75 a 85 é atenção.
export function corDoPercentual(pct) {
  if (pct >= 0.85) return "#047857";
  if (pct >= 0.75) return "#B45309";
  return "#B91C1C";
}
